import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import date from 'date-and-time';
import { AddSongStep } from 'fym-shared/src/music24/catalogAddSong';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLAnalytics } from '../../../analytics/sml/analytics';
import { useCatalogSong } from '../song/useCatalogSong';
import { coverCarryPath } from '../../../coverEditor/publicMode/publicCoverPath';
import { addSongFlowMessages } from './addSongFlowMessages';
import {
  addSongFactsOf,
  itemHasCover,
  openingStep,
  ReleaseChoice,
  stepAfter,
  stepBefore,
  stepMarks,
  STEPPER_STEPS,
} from './addSongFlowSteps';
import { AddSongRoute } from './useAddSongRoute';
import { useSavingCount } from './useSavingCount';
import { bioLinkFactOf, useBioLinkTick } from './useBioLinkTick';
import { FirstSongKind, useOnboardingFirstSong } from './useOnboardingFirstSong';
import { AddSongTopBar, SaveState } from './AddSongTopBar';
import { AddSongBars, AddSongStepper } from './AddSongStepper';
import { SongCard } from './SongCard';
import { SongStep } from './steps/SongStep';
import { ReleaseStep } from './steps/ReleaseStep';
import { CodesStep } from './steps/CodesStep';
import { LinkStep } from './steps/LinkStep';
import { CoverStep } from './steps/CoverStep';
import { AudioStep } from './steps/AudioStep';
import { LyricsStep } from './steps/LyricsStep';
import { DoneStep } from './steps/DoneStep';
import { OnboardingDoneStep } from './steps/OnboardingDoneStep';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddSongPageProps {
  accountId: number;
  artistName: string;
  route: AddSongRoute;
  /** What the Bio Link auto add did for the saved song (the create answer); undefined after a reload. */
  bioLinkResult?: BioLinkAutoAddResult;
  /** Keeps the create answer above this page: the page mounts again when step 1 saves the row. */
  onBioLinkResult: (itemUuid: string, result: BioLinkAutoAddResult) => void;
}

/** Steps a released song does not ask (design c2: the link filled them). */
const RELEASED_SKIPS: AddSongStep[] = ['release', 'codes'];

/**
 * The step-by-step "Add a song" page (design catalog-add-song a-i). One question per step; the
 * song card grows with every answer. Step 1 saves the song, every later answer saves alone, and the
 * address holds the step, so Close, Reload and "Finish setup" lose nothing.
 */
const AddSongPageComponent: React.FC<AddSongPageProps> = ({
  accountId,
  artistName,
  route,
  bioLinkResult,
  onBioLinkResult,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const navigate = useNavigate();
  const { showSuccess } = useShowToast();
  const isOnboarding = route.from === 'onboarding';
  const today = useMemo(() => date.format(new Date(), 'YYYY-MM-DD'), []);
  const catalog = useGetCatalogQuery({ accountId }, { skip: route.itemUuid === undefined });
  const item = useMemo<CatalogItem | undefined>(
    () => catalog.data?.items.find((one) => one.uuid === route.itemUuid),
    [catalog.data, route.itemUuid]
  );
  const { song } = useCatalogSong({ accountId, itemUuid: item?.uuid });
  const saving = useSavingCount();
  const firstSong = useOnboardingFirstSong(accountId);
  const bioTick = useBioLinkTick(accountId);
  const [draftTitle, setDraftTitle] = useState('');
  const [releaseChoice, setReleaseChoice] = useState<ReleaseChoice>();
  const [codesMode, setCodesMode] = useState<'codes' | 'link'>();
  const [hasStarted, setStarted] = useState(false);
  const isClaimed = item?.kind === CatalogItemKind.claimed;
  const step = openingStep(route.requestedStep, item !== undefined);

  // The first answer of step 2 comes from the row when the page opens on a saved song.
  useEffect(() => {
    if (item === undefined || releaseChoice !== undefined) {
      return;
    }
    if (isClaimed) {
      setReleaseChoice('out');
    } else if (item.releaseDate !== null) {
      setReleaseChoice('date');
    }
  }, [isClaimed, item, releaseChoice]);

  // A released song has no release or codes step (the link gave them).
  useEffect(() => {
    if (item !== undefined && isClaimed && RELEASED_SKIPS.includes(step)) {
      route.goTo(itemHasCover(item) ? 'audio' : 'cover', { replace: true });
    }
  }, [isClaimed, item, route, step]);

  // An address with a row that is not in the Catalog (deleted, another artist) starts a new song.
  const isMissing = route.itemUuid !== undefined && catalog.isSuccess && !catalog.isFetching && item === undefined;
  useEffect(() => {
    if (isMissing) {
      route.goTo('song', { itemUuid: '', replace: true });
    }
  }, [isMissing, route]);

  const onStarted = useCallback(
    (kind: FirstSongKind) => {
      if (!isOnboarding || hasStarted) {
        return;
      }
      setStarted(true);
      SMLAnalytics.track('onboarding_add_song_started', { kind }).catch(console.error);
    },
    [hasStarted, isOnboarding]
  );

  const onSaved = useCallback(
    (saved: CatalogItem, kind: FirstSongKind | null, bioLink?: BioLinkAutoAddResult) => {
      if (bioLink !== undefined) {
        onBioLinkResult(saved.uuid, bioLink);
      }
      if (isOnboarding) {
        firstSong.markFirstRelease(saved.uuid, kind ?? 'claim').catch(console.error);
      }
      if (saved.kind === CatalogItemKind.unreleased) {
        route.goTo('release', { itemUuid: saved.uuid, replace: true });
        return;
      }
      route.goTo(itemHasCover(saved) ? 'audio' : 'cover', { itemUuid: saved.uuid, replace: true });
    },
    [firstSong, isOnboarding, onBioLinkResult, route]
  );

  const goNext = useCallback(() => {
    let next = stepAfter(step);
    while (isClaimed && RELEASED_SKIPS.includes(next)) {
      next = stepAfter(next);
    }
    route.goTo(next);
  }, [isClaimed, route, step]);

  const goBack = useCallback(() => {
    let previous = stepBefore(step);
    while (previous !== undefined && isClaimed && RELEASED_SKIPS.includes(previous)) {
      previous = stepBefore(previous);
    }
    if (previous !== undefined) {
      route.goTo(previous);
    }
  }, [isClaimed, route, step]);

  const close = useCallback(() => {
    if (item !== undefined && step !== 'done') {
      const facts = addSongFactsOf(item);
      if (!facts.hasCover || !facts.hasMaster) {
        showSuccess(intl.formatMessage(addSongFlowMessages.closedToast, { title: item.title }));
      }
    }
    if (route.from === 'coverMaker') {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(coverCarryPath());
    } else if (route.from === 'releases') {
      router.push(SMLRoutes.promote);
    } else if (route.from === 'presave') {
      router.push(SMLRoutes.presaveLink, { accountId });
    } else if (route.from === 'studio') {
      router.push(SMLRoutes.promoteStudio, item === undefined ? undefined : { catalogItemUuid: item.uuid });
    } else if (route.from === 'home' || route.from === 'onboarding') {
      router.push(SMLRoutes.home);
    } else {
      router.push(SMLRoutes.catalog);
    }
  }, [accountId, intl, item, navigate, route.from, router, showSuccess, step]);

  // From "Which song is this cover for?": the cover step puts the carried design on this song.
  const putCarriedDesign = useCallback(() => {
    if (item !== undefined) {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(coverCarryPath(item.uuid));
    }
  }, [item, navigate]);

  const addAnother = useCallback(() => route.goTo('song', { itemUuid: '' }), [route]);
  const openStep = useCallback((target: AddSongStep) => route.goTo(target), [route]);
  const showCodes = useCallback(() => setCodesMode('codes'), []);
  const showLink = useCallback(() => setCodesMode('link'), []);
  const onReleaseChoice = useCallback((choice: ReleaseChoice) => {
    setReleaseChoice(choice);
    setCodesMode(undefined);
  }, []);

  const marks = useMemo(() => stepMarks(step, item, song), [item, song, step]);
  let codesLabel: 'both' | 'isrc' | 'link' = 'both';
  if (isClaimed || releaseChoice === 'out') {
    codesLabel = 'link';
  } else if (releaseChoice !== undefined) {
    codesLabel = 'isrc';
  }
  const isLinkMode = codesMode === 'link' || (codesMode === undefined && releaseChoice === 'out');

  let saveState: SaveState = 'saved';
  if (item === undefined) {
    saveState = 'notSaved';
  }
  if (saving.isSaving) {
    saveState = 'saving';
  }

  const stepIndex = STEPPER_STEPS.indexOf(step as (typeof STEPPER_STEPS)[number]);
  const eyebrow = intl.formatMessage(
    step === 'audio' || step === 'lyrics' ? addSongFlowMessages.stepOptional : addSongFlowMessages.stepOf,
    { step: stepIndex + 1, total: STEPPER_STEPS.length }
  );

  const renderStep = () => {
    if (route.itemUuid !== undefined && item === undefined) {
      return <LoadingView />;
    }
    if (step === 'song' || item === undefined) {
      return (
        <SongStep
          accountId={accountId}
          item={item}
          artistName={artistName}
          eyebrow={eyebrow}
          title={item?.title ?? draftTitle}
          onTitleChange={setDraftTitle}
          track={saving.track}
          onSaved={onSaved}
          onNext={goNext}
          onStarted={onStarted}
          bioTick={bioTick}
        />
      );
    }
    switch (step) {
      case 'release':
        return (
          <ReleaseStep
            accountId={accountId}
            item={item}
            eyebrow={eyebrow}
            today={today}
            choice={releaseChoice}
            onChoice={onReleaseChoice}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
          />
        );
      case 'codes':
        return isLinkMode ? (
          <LinkStep
            accountId={accountId}
            item={item}
            artistName={artistName}
            eyebrow={eyebrow}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
            onCodesInstead={showCodes}
          />
        ) : (
          <CodesStep
            key={song === undefined ? 'loading' : 'loaded'}
            accountId={accountId}
            item={item}
            song={song}
            eyebrow={eyebrow}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
            onLinkInstead={showLink}
          />
        );
      case 'cover':
        return (
          <CoverStep
            accountId={accountId}
            item={item}
            eyebrow={eyebrow}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
            onUseCarried={route.from === 'coverMaker' ? putCarriedDesign : undefined}
          />
        );
      case 'audio':
        return (
          <AudioStep
            accountId={accountId}
            item={item}
            song={song}
            eyebrow={eyebrow}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
          />
        );
      case 'lyrics':
        return (
          <LyricsStep
            key={song === undefined ? 'loading' : 'loaded'}
            accountId={accountId}
            item={item}
            song={song}
            eyebrow={eyebrow}
            track={saving.track}
            onBack={goBack}
            onNext={goNext}
            onSkip={goNext}
          />
        );
      case 'done':
        return isOnboarding ? (
          <OnboardingDoneStep accountId={accountId} item={item} />
        ) : (
          <DoneStep
            accountId={accountId}
            item={item}
            today={today}
            onOpenStep={openStep}
            bioLinkResult={bioLinkResult}
            onAddAnother={addAnother}
            onClose={close}
            closeLabel={intl.formatMessage(
              route.from === 'coverMaker' ? addSongFlowMessages.doneBackDesign : addSongFlowMessages.doneBack
            )}
          />
        );
    }
  };

  return (
    // `data-m24-full-page`: the app top bar hides on this page (see CatalogAddSong.module.scss).
    <div className={styles.page} data-testid="add-song-page" data-m24-full-page="addSong">
      <AddSongTopBar
        isOnboarding={isOnboarding}
        saveState={saveState}
        onClose={close}
        stepper={
          <AddSongStepper marks={marks} codesLabel={codesLabel} onOpen={item === undefined ? undefined : openStep} />
        }
      />
      <AddSongBars marks={marks} />
      <main className={styles.body}>
        <section className={styles.qa} aria-live="polite">
          {renderStep()}
        </section>
        <SongCard
          item={item}
          song={song}
          draftTitle={draftTitle}
          artistName={artistName}
          today={today}
          bioLinkFact={bioLinkFactOf(item, bioTick, bioLinkResult)}
        />
      </main>
    </div>
  );
};

export const AddSongPage = TypedMemo(AddSongPageComponent);
